import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  FileText, Clock, MapPin, Video, Users, UserCheck, Paperclip, Activity,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { calendarAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, formatDate, getErrorMessage } from '@/lib/utils';
import { formatEventDateTime } from '@/lib/eventTime';
import LifecycleActions from './LifecycleActions';
import VisibilityPreviewPanel from './VisibilityPreviewPanel';
import ParticipantsPanel from './ParticipantsPanel';
import AttachmentsPanel from './AttachmentsPanel';
import EventSlotsPanel from './EventSlotsPanel';
import AddToCalendar from './AddToCalendar';
import EventCover from './EventCover';
import useInternalCoverBlob from './useInternalCoverBlob';
import DrawerSectionNav, { DrawerSection } from './DrawerSectionNav';
import { eventTypeLabel, businessCategoryLabel, languageLabel, VISIBILITY_SCOPES } from './eventFormHelpers';

const STATUS_TONE = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  published: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-rose-100 text-rose-700 border-rose-200',
  completed: 'bg-sky-100 text-sky-700 border-sky-200',
  archived: 'bg-slate-100 text-slate-500 border-slate-200',
};

const scopeLabel = (v) =>
  VISIBILITY_SCOPES.find((s) => s.value === v)?.label?.split(' — ')[0] || v || '—';

const SECTIONS = [
  { id: 'overview', label: 'Overview', icon: FileText },
  { id: 'schedule', label: 'Schedule', icon: Clock },
  { id: 'location', label: 'Location', icon: MapPin },
  { id: 'audience', label: 'Audience', icon: Users },
  { id: 'registration', label: 'Registration', icon: UserCheck },
  { id: 'attachments', label: 'Attachments', icon: Paperclip },
  { id: 'timeline', label: 'Activity', icon: Activity },
];

export default function EventDetailDrawer({ eventId, onClose, onChanged, onEdit }) {
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const [detail, setDetail] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [regSummary, setRegSummary] = useState(null);
  const [regBusy, setRegBusy] = useState(false);

  const refetch = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const [ev, parts, atts, prev, reg] = await Promise.all([
        calendarAPI.getEvent(eventId, { include_archived: true }).then((r) => r.data),
        calendarAPI.listParticipants(eventId).then((r) => r.data).catch(() => []),
        calendarAPI.listAttachments(eventId).then((r) => r.data).catch(() => []),
        isKR ? calendarAPI.previewVisibility(eventId, {}).then((r) => r.data).catch(() => null) : Promise.resolve(null),
        calendarAPI.registrationSummary(eventId).then((r) => r.data).catch(() => null),
      ]);
      setDetail(ev);
      setParticipants(parts);
      setAttachments(atts);
      setPreview(prev);
      setRegSummary(reg);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load event'));
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [eventId, isKR, onClose]);

  useEffect(() => {
    if (!eventId) { setDetail(null); return; }
    refetch();
  }, [eventId, refetch]);

  const doRegister = useCallback(async (opts = {}) => {
    setRegBusy(true);
    try {
      await calendarAPI.register(eventId, opts);
      toast.success('You are registered');
      await refetch();
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Registration failed'));
    } finally {
      setRegBusy(false);
    }
  }, [eventId, refetch, onChanged]);

  const doRsvp = useCallback(async (response) => {
    setRegBusy(true);
    try {
      await calendarAPI.rsvp(eventId, response);
      toast.success(`RSVP updated: ${response}`);
      await refetch();
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'RSVP change failed'));
    } finally {
      setRegBusy(false);
    }
  }, [eventId, refetch, onChanged]);

  const doCancelRegistration = useCallback(async () => {
    if (!window.confirm('Cancel your registration for this event?')) return;
    setRegBusy(true);
    try {
      await calendarAPI.cancelRegistration(eventId);
      toast.success('Registration cancelled');
      await refetch();
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Cancellation failed'));
    } finally {
      setRegBusy(false);
    }
  }, [eventId, refetch, onChanged]);

  const canManage = isKR || (detail && detail.created_by_user_id === user?.user_id);

  const refetchParticipants = useCallback(async () => {
    if (!eventId) return;
    try {
      const parts = await calendarAPI.listParticipants(eventId).then((r) => r.data);
      setParticipants(parts);
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Refresh failed'));
    }
  }, [eventId, onChanged]);

  const doLifecycle = async (action, reason) => {
    try {
      if (action === 'publish') await calendarAPI.publish(eventId);
      else if (action === 'cancel') await calendarAPI.cancel(eventId, reason);
      else if (action === 'complete') await calendarAPI.complete(eventId);
      else if (action === 'archive') await calendarAPI.archive(eventId, reason);
      toast.success(`Event ${action}d`);
      await refetch();
      onChanged?.();
    } catch (e) {
      const detailErr = e?.response?.data?.detail;
      if (detailErr && typeof detailErr === 'object' && detailErr.publication_blocked) {
        toast.error(`Publish blocked: ${detailErr.publication_blocked.join('; ')}`);
      } else {
        toast.error(getErrorMessage(e, `${action} failed`));
      }
    }
  };

  const participantsBadge = participants.length > 0
    ? <span className="text-[10px] font-semibold text-slate-500">{participants.length}</span>
    : null;
  const attachmentsBadge = attachments.length > 0
    ? <span className="text-[10px] font-semibold text-slate-500">{attachments.length}</span>
    : null;

  const sections = SECTIONS.map((s) => {
    if (s.id === 'audience') return { ...s, badge: participantsBadge };
    if (s.id === 'attachments') return { ...s, badge: attachmentsBadge };
    return s;
  }).filter((s) => s.id !== 'slots' || detail?.slots_enabled);
  if (detail?.slots_enabled && !sections.some((s) => s.id === 'slots')) {
    const idx = sections.findIndex((s) => s.id === 'registration');
    sections.splice(idx >= 0 ? idx + 1 : sections.length, 0, { id: 'slots', label: 'Slots', icon: Clock });
  }

  const locationStr = detail?.physical_location
    ? [detail.physical_location.street, detail.physical_location.city, detail.physical_location.state, detail.physical_location.zip]
        .filter(Boolean).join(', ')
    : '';

  return (
    <Sheet open={!!eventId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-4xl w-[90vw] p-0 flex flex-col" data-testid="calendar-detail-drawer">
        {/* Accessible title/description — always rendered so Radix a11y is
            satisfied even during the loading state. The visible header
            below duplicates the title once the detail loads. */}
        <SheetHeader className="sr-only">
          <SheetTitle>{detail?.title || 'Event details'}</SheetTitle>
          <SheetDescription>
            {detail
              ? `Event details for ${detail.title}. Use the left navigation to jump between sections.`
              : 'Loading event details.'}
          </SheetDescription>
        </SheetHeader>

        {loading || !detail ? (
          <div className="py-16 text-center text-sm text-slate-500">Loading…</div>
        ) : (
          <>
            {/* Visible header (duplicated title for visual hierarchy) */}
            <div className="px-6 pt-6 pb-4 border-b border-slate-200 shrink-0 space-y-3">
              {/* Phase 26.9 — compact cover thumbnail inside the drawer.
                  Left as a 40%-width thumb so the header remains scannable.
                  For internal events the cover is fetched with auth and
                  swapped in as a blob URL — <img> alone can't attach
                  bearer tokens. */}
              {detail.event_id && (
                <div className="w-40" data-testid="calendar-detail-cover">
                  <DrawerCoverThumb detail={detail} key={detail?.cover_image_url || 'none'} />
                </div>
              )}
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`text-[10px] border ${STATUS_TONE[detail.status] || ''}`} data-testid="calendar-detail-status">
                    {detail.status}
                  </Badge>
                  {/* Phase 26.8 — Internal / Public badge (derived from
                      visibility_scope; source of truth stays on the server). */}
                  {detail.visibility && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${
                        detail.visibility === 'public'
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : 'bg-slate-100 border-slate-200 text-slate-700'
                      }`}
                      data-testid="calendar-detail-visibility"
                    >
                      {detail.visibility === 'public' ? 'Public' : 'Internal'}
                    </Badge>
                  )}
                  {detail.owner_type === 'agency' && (
                    <Badge
                      variant="outline"
                      className="text-[10px] bg-amber-50 border-amber-200 text-amber-800"
                      data-testid="calendar-detail-owner-agency"
                    >
                      Agency-owned
                    </Badge>
                  )}
                  {detail.event_type && (
                    <Badge variant="outline" className="text-[10px]" data-testid="calendar-detail-event-type">
                      {eventTypeLabel(detail.event_type)}
                    </Badge>
                  )}
                  {detail.business_category && (
                    <Badge variant="outline" className="text-[10px] bg-sky-50 border-sky-200 text-sky-700" data-testid="calendar-detail-business-category">
                      {businessCategoryLabel(detail.business_category)}
                    </Badge>
                  )}
                  {detail.language && (
                    <Badge variant="outline" className="text-[10px] bg-violet-50 border-violet-200 text-violet-700" data-testid="calendar-detail-language">
                      {languageLabel(detail.language)}
                    </Badge>
                  )}
                  <span className="text-[11px] uppercase tracking-wider text-slate-500">
                    {detail.category}
                  </span>
                </div>
                <h2 className="text-lg font-semibold text-slate-900" data-testid="calendar-detail-title">
                  {detail.title}
                </h2>
              </div>

              {isKR && (
                <div className="flex flex-wrap items-center gap-2" data-testid="calendar-detail-admin-toolbar">
                  {/* Phase 27.30.11 — quick jump into the tabbed workspace. */}
                  <Button
                    size="sm"
                    variant="default"
                    className="bg-sky-600 hover:bg-sky-700"
                    onClick={() => window.open(`/events/${detail.event_id}/workspace`, '_self')}
                    data-testid="calendar-detail-open-workspace-btn"
                  >
                    Open workspace →
                  </Button>
                  {detail.status !== 'archived' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onEdit?.(detail)}
                      data-testid="calendar-detail-edit-btn"
                    >
                      Edit
                    </Button>
                  )}
                  <LifecycleActions
                    status={detail.status}
                    publicationReady={preview?.publication_ready}
                    onAction={doLifecycle}
                  />
                </div>
              )}
            </div>

            {/* Body: sticky left nav + scrollable content */}
            <div className="flex-1 min-h-0">
              <DrawerSectionNav sections={sections} testId="event-detail-nav">
                {/* Overview ────────────────────────────────── */}
                <DrawerSection id="overview" title="Overview">
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <DetailKV label="Event Type" value={detail.event_type ? eventTypeLabel(detail.event_type) : <span className="text-slate-400">Not set</span>} />
                    <DetailKV label="Business Category" value={detail.business_category ? businessCategoryLabel(detail.business_category) : <span className="text-slate-400">Not set</span>} />
                    <DetailKV label="Language" value={detail.language ? languageLabel(detail.language) : <span className="text-slate-400">Not specified</span>} />
                    <DetailKV label="Category (legacy)" value={detail.category} />
                    <DetailKV label="Carrier" value={detail.carrier_id || <span className="text-slate-400">—</span>} />
                    <DetailKV label="Visibility" value={scopeLabel(detail.visibility_scope)} />
                    <DetailKV label="Requires RSVP" value={detail.requires_rsvp ? 'Yes' : 'No'} />
                  </dl>

                  {detail.description && (
                    <div className="pt-2">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Description</div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{detail.description}</p>
                    </div>
                  )}

                  {detail.internal_notes && (
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs" data-testid="calendar-internal-notes">
                      <div className="font-semibold text-amber-800 mb-1">Internal notes (KR only)</div>
                      <div className="text-amber-900 whitespace-pre-wrap">{detail.internal_notes}</div>
                    </div>
                  )}
                </DrawerSection>

                {/* Schedule ────────────────────────────────── */}
                <DrawerSection id="schedule" title="Schedule">
                  <div className="space-y-2 text-sm text-slate-700">
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-slate-500" />
                      <span className="font-medium">Starts:</span>
                      <span>{formatEventDateTime(detail.start_datetime, detail.timezone)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-slate-500" />
                      <span className="font-medium">Ends:</span>
                      <span>{formatEventDateTime(detail.end_datetime, detail.timezone)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600 text-xs">
                      <span className="font-medium">Timezone:</span>
                      <span>{detail.timezone}</span>
                      {detail.all_day && <Badge variant="outline" className="text-[10px]">All-day</Badge>}
                    </div>
                  </div>
                  {/* Phase 26.7 — Add to calendar (internal). Only shown for
                      events that are visible to the caller and not archived. */}
                  {detail.status !== 'draft' && detail.status !== 'archived' && (
                    <div className="pt-3 border-t border-slate-100">
                      <AddToCalendar variant="internal" eventId={detail.event_id} />
                    </div>
                  )}
                </DrawerSection>

                {/* Location ────────────────────────────────── */}
                <DrawerSection id="location" title="Location">
                  {(!locationStr && !detail.virtual_meeting_url) && (
                    <p className="text-xs text-slate-500">No location or virtual meeting URL provided.</p>
                  )}
                  {locationStr && (
                    <div className="flex items-start gap-2 text-sm text-slate-700">
                      <MapPin size={14} className="mt-0.5 text-slate-500" />
                      <span>{locationStr}</span>
                    </div>
                  )}
                  {detail.physical_location?.room && (
                    <p className="text-xs text-slate-500 ml-6">Room: {detail.physical_location.room}</p>
                  )}
                  {detail.virtual_meeting_url && (
                    <div className="flex items-center gap-2 text-sm">
                      <Video size={14} className="text-slate-500" />
                      <a href={detail.virtual_meeting_url} target="_blank" rel="noreferrer" className="text-sky-600 hover:underline break-all">
                        {detail.virtual_meeting_url}
                      </a>
                    </div>
                  )}
                </DrawerSection>

                {/* Audience ────────────────────────────────── */}
                <DrawerSection id="audience" title="Audience & Participants">
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <DetailKV label="Visibility scope" value={scopeLabel(detail.visibility_scope)} />
                    {detail.visibility_scope === 'agency' && (
                      <>
                        <DetailKV label="Agency" value={detail.audience_agency_id || '—'} />
                        <DetailKV label="Inherit to downline" value={detail.inherit_to_downline ? 'Yes' : 'No'} />
                      </>
                    )}
                    {detail.visibility_scope === 'specific_users' && (
                      <DetailKV label="Named users" value={(detail.audience_user_ids || []).length} />
                    )}
                  </dl>

                  <ParticipantsPanel
                    event={detail}
                    participants={participants}
                    currentUser={user}
                    canManage={canManage}
                    onChanged={refetchParticipants}
                  />

                  {isKR && preview && (
                    <div className="pt-2">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Visibility preview</div>
                      <VisibilityPreviewPanel preview={preview} mode="saved" />
                    </div>
                  )}
                </DrawerSection>

                {/* Registration ─────────────────────────────── */}
                <DrawerSection id="registration" title="Registration">
                  <RegistrationPanel
                    detail={detail}
                    summary={regSummary}
                    busy={regBusy}
                    onRegister={doRegister}
                    onRsvp={doRsvp}
                    onCancel={doCancelRegistration}
                    isKR={isKR}
                  />
                </DrawerSection>

                {/* Slots ─────────────────────────────── */}
                {detail?.slots_enabled && (
                  <DrawerSection id="slots" title="Participation Slots">
                    <EventSlotsPanel eventId={detail.event_id} timezone={detail.timezone} onConfigChanged={refetch} />
                  </DrawerSection>
                )}

                {/* Attachments ─────────────────────────────── */}
                <DrawerSection id="attachments" title="Attachments">
                  <AttachmentsPanel
                    event={detail}
                    attachments={attachments}
                    canManage={canManage}
                    isKR={isKR}
                    onChanged={refetch}
                  />
                </DrawerSection>

                {/* Timeline ────────────────────────────────── */}
                <DrawerSection id="timeline" title="Activity Timeline">
                  <ol className="relative space-y-3 pl-4 border-l border-slate-200" data-testid="calendar-detail-timeline">                    <TimelineEntry
                      when={detail.created_at}
                      title="Event created"
                      subtitle={detail.created_by_user_id ? `by ${detail.created_by_user_id}` : null}
                      tone="slate"
                    />
                    {detail.published_at && (
                      <TimelineEntry when={detail.published_at} title="Published" tone="emerald" />
                    )}
                    {detail.cancelled_at && (
                      <TimelineEntry
                        when={detail.cancelled_at}
                        title="Cancelled"
                        subtitle={detail.cancelled_reason}
                        tone="rose"
                      />
                    )}
                    {/* Phase 26.3 — attendance events derived from participants list */}
                    {buildAttendanceTimeline(participants).map((e) => (
                      <TimelineEntry
                        key={e.key}
                        when={e.when}
                        title={e.title}
                        subtitle={e.subtitle}
                        tone={e.tone}
                      />
                    ))}
                    {detail.updated_at && detail.updated_at !== detail.created_at && (
                      <TimelineEntry
                        when={detail.updated_at}
                        title="Last updated"
                        subtitle={detail.updated_by_user_id ? `by ${detail.updated_by_user_id}` : null}
                        tone="sky"
                      />
                    )}
                  </ol>
                  {/* Phase 26.5 — Notification History (managers only) */}
                  {isKR && (
                    <NotificationHistoryBlock eventId={detail.event_id} />
                  )}
                  {detail.cancelled_reason && (
                    <div className="mt-4 rounded-md bg-rose-50 border border-rose-200 p-3 text-xs" data-testid="calendar-cancelled-reason">
                      <div className="font-semibold text-rose-800 mb-1">Cancellation reason</div>
                      <div className="text-rose-900 whitespace-pre-wrap">{detail.cancelled_reason}</div>
                    </div>
                  )}
                  {detail.notification_meta && (
                    <div className="mt-4 space-y-1 text-xs text-slate-500">
                      <div className="font-semibold text-slate-600 text-[11px] uppercase tracking-wider">Notification ledger</div>
                      {detail.notification_meta.published_at_notified && (
                        <div>Published notification sent: {formatDate(detail.notification_meta.published_at_notified)}</div>
                      )}
                      {detail.notification_meta.cancelled_at_notified && (
                        <div>Cancellation notification sent: {formatDate(detail.notification_meta.cancelled_at_notified)}</div>
                      )}
                      {(detail.notification_meta.reminders_sent || []).length > 0 && (
                        <div>Reminders sent: {detail.notification_meta.reminders_sent.length}</div>
                      )}
                      {!detail.notification_meta.published_at_notified &&
                       !detail.notification_meta.cancelled_at_notified &&
                       (detail.notification_meta.reminders_sent || []).length === 0 && (
                        <div className="text-slate-400">No notifications sent yet.</div>
                      )}
                    </div>
                  )}
                </DrawerSection>
              </DrawerSectionNav>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

const DetailKV = ({ label, value }) => (
  <div>
    <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</dt>
    <dd className="text-sm text-slate-800 mt-0.5">{value}</dd>
  </div>
);

const TONE_DOT = {
  slate: 'bg-slate-400',
  emerald: 'bg-emerald-500',
  rose: 'bg-rose-500',
  sky: 'bg-sky-500',
};

// Phase 26.5 — Compact notification history + resend inside the timeline.
const STATUS_BADGE = {
  scheduled: 'bg-slate-100 text-slate-700 border-slate-200',
  processing: 'bg-sky-100 text-sky-700 border-sky-200',
  sent: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  failed: 'bg-rose-100 text-rose-700 border-rose-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
  skipped: 'bg-slate-100 text-slate-500 border-slate-200',
};

function NotificationHistoryBlock({ eventId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(null);
  // Broadcast diagnostic — lazily loaded when the user expands the panel.
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagExpanded, setDiagExpanded] = useState(false);
  const [checkEmail, setCheckEmail] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await calendarAPI.notificationsHistory(eventId, { page_size: 50 });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const loadDiag = useCallback(async () => {
    setDiagLoading(true);
    try {
      const { data } = await calendarAPI.publishBroadcastDiagnostic(eventId);
      setDiag(data);
    } catch {
      setDiag(null);
    } finally {
      setDiagLoading(false);
    }
  }, [eventId]);

  const runCheck = async () => {
    const q = checkEmail.trim();
    if (!q) return;
    setChecking(true);
    setCheckResult(null);
    try {
      const { data } = await calendarAPI.publishBroadcastDiagnostic(eventId, q);
      setCheckResult(data.check_result || null);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Diagnostic lookup failed'));
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (diagExpanded && !diag && !diagLoading) loadDiag();
  }, [diagExpanded, diag, diagLoading, loadDiag]);

  const doResend = async (id) => {
    if (!window.confirm('Resend this notification to the recipient?')) return;
    setResending(id);
    try {
      await calendarAPI.notificationResend(eventId, id);
      toast.success('Resend queued');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Resend failed'));
    } finally {
      setResending(null);
    }
  };

  return (
    <div className="mt-6 pt-4 border-t border-slate-200" data-testid="notification-history-block">
      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Notification History</h4>
      {loading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-500">No emails sent yet.</p>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded max-h-64" data-testid="notification-history-table">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-2 py-1.5">Recipient</th>
                <th className="px-2 py-1.5">Type</th>
                <th className="px-2 py-1.5">Status</th>
                <th className="px-2 py-1.5">When</th>
                <th className="px-2 py-1.5 w-20">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((n) => (
                <tr key={n.id} data-testid={`notif-row-${n.id}`}>
                  <td className="px-2 py-1.5 text-slate-800 truncate max-w-[180px]">{n.recipient_email}</td>
                  <td className="px-2 py-1.5 text-slate-600">{n.trigger_type.replace(/_/g, ' ')}</td>
                  <td className="px-2 py-1.5">
                    <Badge className={`text-[10px] border ${STATUS_BADGE[n.status] || ''}`}>{n.status}</Badge>
                    {n.failure_reason && <span className="text-[10px] text-rose-600 ml-1" title={n.failure_reason}>· failed</span>}
                  </td>
                  <td className="px-2 py-1.5 text-slate-500 text-[11px]">
                    {n.sent_at ? new Date(n.sent_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
                      : n.failed_at ? new Date(n.failed_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
                      : new Date(n.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                  </td>
                  <td className="px-2 py-1.5">
                    <Button
                      size="sm" variant="outline"
                      className="h-6 text-[11px]"
                      disabled={resending === n.id}
                      onClick={() => doResend(n.id)}
                      data-testid={`notif-resend-${n.id}`}
                    >
                      Resend
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Broadcast diagnostic — helps admins figure out why a specific
          agent did NOT receive the event_published email. */}
      <div className="mt-3 rounded-md border border-slate-200 bg-slate-50/60" data-testid="publish-broadcast-diagnostic">
        <button
          type="button"
          onClick={() => setDiagExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 text-xs text-slate-700 hover:bg-slate-100"
          data-testid="publish-broadcast-diagnostic-toggle"
        >
          <span className="font-semibold">Broadcast Diagnostic — why didn&apos;t someone get this event?</span>
          <span className="text-slate-500">{diagExpanded ? '▾' : '▸'}</span>
        </button>
        {diagExpanded && (
          <div className="px-3 pb-3 pt-1 space-y-3 text-xs">
            {diagLoading ? (
              <p className="text-slate-500">Loading diagnostic…</p>
            ) : diag ? (
              <>
                <div className="grid grid-cols-2 gap-2 rounded bg-white border border-slate-200 p-2">
                  <div><span className="text-slate-500">Scope:</span> <strong>{diag.visibility_scope || '(none)'}</strong></div>
                  <div><span className="text-slate-500">Email enabled:</span> <strong>{diag.email_notifications_enabled ? 'yes' : 'no'}</strong></div>
                  <div><span className="text-slate-500">Audience count:</span> <strong data-testid="diag-audience-count">{diag.resolved_audience_count}</strong></div>
                  <div><span className="text-slate-500">Delivered:</span> <strong data-testid="diag-delivered-count">{diag.delivered_count}</strong>{diag.failed_count > 0 && <span className="ml-1 text-rose-600">· {diag.failed_count} failed</span>}</div>
                  {diag.visibility_scope === 'agency' && (
                    <div className="col-span-2 text-slate-500">
                      Target agency: <strong className="text-slate-700">{diag.audience_agency_id}</strong>
                      {diag.inherit_to_downline && <span className="ml-1">(+ downline)</span>}
                    </div>
                  )}
                </div>
                {diag.resolved_audience_sample?.length > 0 && (
                  <details className="rounded bg-white border border-slate-200 px-2 py-1">
                    <summary className="cursor-pointer text-slate-700 font-medium">
                      First {diag.resolved_audience_sample.length} of {diag.resolved_audience_count} audience recipients
                    </summary>
                    <div className="mt-2 max-h-40 overflow-y-auto space-y-0.5 font-mono text-[10.5px] text-slate-600" data-testid="diag-audience-sample">
                      {diag.resolved_audience_sample.map((u) => (
                        <div key={u.email}>{u.email} <span className="text-slate-400">— {u.name}</span></div>
                      ))}
                    </div>
                  </details>
                )}
                {diag.failed_sample?.length > 0 && (
                  <details className="rounded bg-white border border-rose-200 px-2 py-1" open>
                    <summary className="cursor-pointer text-rose-700 font-medium">
                      {diag.failed_count} failed delivery/deliveries
                    </summary>
                    <div className="mt-2 max-h-40 overflow-y-auto space-y-0.5 font-mono text-[10.5px] text-rose-700">
                      {diag.failed_sample.map((r) => (
                        <div key={r.email}>{r.email} <span className="text-slate-400">— {r.reason || 'unknown'}</span></div>
                      ))}
                    </div>
                  </details>
                )}
                <div className="pt-2 border-t border-slate-200">
                  <div className="text-slate-600 mb-1">
                    Check a specific email — did they get it? Why or why not?
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="agent@example.com"
                      value={checkEmail}
                      onChange={(e) => setCheckEmail(e.target.value)}
                      className="flex-1 h-8 rounded border border-slate-300 px-2 text-xs"
                      data-testid="diag-check-email-input"
                    />
                    <Button
                      size="sm"
                      onClick={runCheck}
                      disabled={!checkEmail.trim() || checking}
                      data-testid="diag-check-email-btn"
                    >
                      {checking ? 'Checking…' : 'Check'}
                    </Button>
                  </div>
                  {checkResult && (
                    <div
                      className={`mt-2 rounded border px-2 py-1.5 ${
                        checkResult.included
                          ? (checkResult.delivery_status === 'sent'
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                              : 'bg-amber-50 border-amber-200 text-amber-800')
                          : 'bg-rose-50 border-rose-200 text-rose-800'
                      }`}
                      data-testid="diag-check-result"
                    >
                      {checkResult.included ? (
                        <div>
                          <strong>Included in audience.</strong>{' '}
                          Delivery status: <code>{checkResult.delivery_status || 'unknown'}</code>
                          {checkResult.sent_at && <> — sent {new Date(checkResult.sent_at).toLocaleString()}</>}
                          {checkResult.failure_reason && <div className="text-rose-700 mt-1">Failure: {checkResult.failure_reason}</div>}
                          {checkResult.detail && <div className="mt-1">{checkResult.detail}</div>}
                        </div>
                      ) : (
                        <div>
                          <strong>Not included.</strong> Reason: <code>{checkResult.reason}</code>
                          {checkResult.detail && <div className="mt-1">{checkResult.detail}</div>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className="text-slate-500">Could not load diagnostic. You may not have permission.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const TimelineEntry = ({ when, title, subtitle, tone = 'slate' }) => (
  <li className="relative">
    <span className={`absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full ${TONE_DOT[tone] || TONE_DOT.slate} ring-2 ring-white`} aria-hidden />
    <div className="text-sm text-slate-800">{title}</div>
    <div className="text-[11px] text-slate-500">
      {when ? formatDate(when) : '—'}
      {subtitle ? ` · ${subtitle}` : ''}
    </div>
  </li>
);

// Phase 26.2A — Registration status + one-click actions.
const RSVP_TONE = {
  going: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  maybe: 'bg-amber-100 text-amber-700 border-amber-200',
  declined: 'bg-slate-200 text-slate-700 border-slate-300',
};

function RegistrationPanel({ detail, summary, busy, onRegister, onRsvp, onCancel, isKR }) {
  const cap = summary?.capacity ?? detail?.capacity ?? null;
  const used = summary?.total_seats_used ?? 0;
  const remaining = summary?.remaining_seats ?? (cap == null ? null : Math.max(0, cap - used));
  const my = summary?.my_registration || null;
  const isRegistered = my?.registration_status === 'registered';
  const isCancelled = my?.registration_status === 'cancelled';
  const rsvp = my?.rsvp_response;
  const deadlinePassed = !!summary?.deadline_passed;
  const allowLate = !!summary?.allow_late_registration;
  const allowGuests = !!summary?.registration_allow_guests;
  const maxGuests = summary?.max_guests_per_registration || 0;
  const [guestCount, setGuestCount] = useState(0);

  const pct = cap && cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const barColor = pct >= 100 ? 'bg-rose-500' : pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
  const isFull = cap != null && remaining === 0;
  const canRegister = !isRegistered && !isFull && (!deadlinePassed || allowLate || isKR);

  const registerBtnLabel = deadlinePassed && !allowLate && isKR
    ? 'Register (Admin override)'
    : (isCancelled ? 'Register again' : 'Register me');

  return (
    <div className="space-y-4" data-testid="registration-panel">
      {/* Capacity bar */}
      <div className="rounded-md border border-slate-200 bg-white p-4 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-slate-800">Capacity</span>
          <span className="text-slate-600 text-xs">
            {cap == null ? (
              <span>{used} registered · <span className="text-slate-400">Unlimited</span></span>
            ) : (
              <>
                <span data-testid="reg-panel-registered">{used}</span> / <span data-testid="reg-panel-capacity">{cap}</span> · <span data-testid="reg-panel-remaining">{remaining}</span> seats left
              </>
            )}
          </span>
        </div>
        {cap != null && (
          <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
          </div>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 pt-1">
          {summary?.registration_deadline && (
            <span data-testid="reg-panel-deadline">
              Deadline: {formatDate(summary.registration_deadline)}
              {deadlinePassed && <span className="text-rose-600 ml-1">· passed</span>}
              {deadlinePassed && allowLate && <span className="text-amber-600 ml-1">· late reg allowed</span>}
            </span>
          )}
          {allowGuests && (
            <span>Guests allowed: up to {maxGuests}</span>
          )}
        </div>
      </div>

      {/* My registration status */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="my-registration-block">
        {isRegistered ? (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border">Registered</Badge>
              {rsvp && (
                <Badge className={`${RSVP_TONE[rsvp]} border capitalize`} data-testid="my-rsvp-badge">
                  {rsvp}
                </Badge>
              )}
              {my.guest_count > 0 && (
                <Badge variant="outline" className="text-xs">+{my.guest_count} guest{my.guest_count === 1 ? '' : 's'}</Badge>
              )}
            </div>
            <div className="text-xs text-slate-600">
              {my.registered_at && <>Registered on {formatDate(my.registered_at)}.</>}
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" variant={rsvp === 'going' ? 'default' : 'outline'} disabled={busy || rsvp === 'going'} onClick={() => onRsvp('going')} data-testid="rsvp-going-btn">Going</Button>
              <Button size="sm" variant={rsvp === 'maybe' ? 'default' : 'outline'} disabled={busy || rsvp === 'maybe'} onClick={() => onRsvp('maybe')} data-testid="rsvp-maybe-btn">Maybe</Button>
              <Button size="sm" variant={rsvp === 'declined' ? 'default' : 'outline'} disabled={busy || rsvp === 'declined'} onClick={() => onRsvp('declined')} data-testid="rsvp-decline-btn">Decline</Button>
              <Button size="sm" variant="outline" className="ml-auto text-rose-600 hover:text-rose-700" disabled={busy} onClick={onCancel} data-testid="cancel-registration-btn">Cancel registration</Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-xs">
                {isCancelled ? 'Registration cancelled' : 'Not registered'}
              </Badge>
              {isFull && <Badge className="bg-rose-100 text-rose-800 border-rose-200 border">Event is full</Badge>}
              {deadlinePassed && !allowLate && !isKR && (
                <Badge className="bg-slate-100 text-slate-700 border-slate-300 border">Deadline passed</Badge>
              )}
            </div>
            {allowGuests && canRegister && (
              <div className="flex items-center gap-2 text-xs pt-1">
                <label className="text-slate-600">Guests:</label>
                <input
                  type="number" min={0} max={maxGuests}
                  value={guestCount}
                  onChange={(e) => setGuestCount(Math.max(0, Math.min(maxGuests, Number(e.target.value) || 0)))}
                  className="w-16 rounded-md border border-slate-300 px-2 py-1"
                  data-testid="register-guest-count-input"
                />
                <span className="text-slate-500">(max {maxGuests})</span>
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                size="sm"
                disabled={busy || !canRegister}
                onClick={() => onRegister({
                  guest_count: allowGuests ? guestCount : 0,
                  force: deadlinePassed && !allowLate && isKR,
                })}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="register-btn"
              >
                {registerBtnLabel}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Aggregate counters (managers/anyone) */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="reg-panel-counts">
          <Counter label="Registered" value={summary.counts.registered} />
          <Counter label="Going" value={summary.counts.going} />
          <Counter label="Maybe" value={summary.counts.maybe} />
          <Counter label="Declined" value={summary.counts.declined} />
          <Counter label="Waitlisted" value={summary.counts.waitlisted} tone="purple" />
          <Counter label="Cancelled" value={summary.counts.cancelled} />
          <Counter label="Checked In" value={summary.counts.attended} tone="emerald" />
          <Counter label="No Show" value={summary.counts.no_show} tone="amber" />
          <Counter label="Walk-in" value={summary.counts.walk_in} tone="violet" />
        </div>
      )}
    </div>
  );
}

const Counter = ({ label, value, tone }) => (
  <div className={`rounded-md border px-3 py-2 ${
    tone === 'emerald' ? 'border-emerald-200 bg-emerald-50' :
    tone === 'amber' ? 'border-amber-200 bg-amber-50' :
    tone === 'violet' ? 'border-violet-200 bg-violet-50' :
    tone === 'purple' ? 'border-purple-200 bg-purple-50' :
    'border-slate-200 bg-white'
  }`}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-sm font-semibold text-slate-800">{value ?? 0}</div>
  </div>
);

// Phase 26.3 — Build attendance-derived timeline entries from the loaded
// participants list. Deterministic client-side derivation — no backend
// audit fetch required at this stage.
function buildAttendanceTimeline(participants) {
  if (!Array.isArray(participants) || participants.length === 0) return [];
  const entries = [];
  for (const p of participants) {
    const who = p.registered_by_name
      || (p.user_id ? p.user_id : [p.guest_first_name, p.guest_last_name].filter(Boolean).join(' '))
      || 'Participant';
    // Walk-in creation.
    if (p.attendance_status === 'walk_in' && p.checked_in_at) {
      entries.push({
        key: `walkin-${p.assignment_id}`,
        when: p.checked_in_at,
        title: 'Walk-in added',
        subtitle: `${p.guest_first_name || ''} ${p.guest_last_name || ''}`.trim() || who,
        tone: 'violet',
      });
      continue;
    }
    // Regular check-in.
    if (p.attendance_status === 'attended' && p.checked_in_at) {
      entries.push({
        key: `ci-${p.assignment_id}`,
        when: p.checked_in_at,
        title: 'Checked in',
        subtitle: p.checked_in_by_name ? `${who} · by ${p.checked_in_by_name}` : who,
        tone: 'emerald',
      });
    }
    // No-show — no dedicated timestamp exists, so anchor on rsvp_at as a
    // best-effort ordering signal; if absent, use assigned_at.
    if (p.attendance_status === 'no_show') {
      entries.push({
        key: `noshow-${p.assignment_id}`,
        when: p.rsvp_at || p.assigned_at,
        title: 'Marked no-show',
        subtitle: who,
        tone: 'amber',
      });
    }
  }
  // Sort ascending by timestamp; TimelineEntry formats each cleanly.
  entries.sort((a, b) => new Date(a.when || 0) - new Date(b.when || 0));
  return entries;
}

// Phase 26.9 — internal-safe cover thumb (uses the blob-URL hook so
// bearer auth works on the private cover endpoint).
function DrawerCoverThumb({ detail }) {
  const url = useInternalCoverBlob(detail?.cover_image_url);
  return <EventCover coverUrl={url} title={detail?.title || 'Event'} category={detail?.category} variant="thumb" />;
}

